import React, { useState, useRef } from 'react';

export default function AgentAvatar({ name, size = 40, imageUrl, isMobile = false }) {
  const [imageError, setImageError] = useState(false);
  const [activeFace, setActiveFace] = useState(0); // 0: Headshot, 1: Balloon, 2: JS Logo
  const [cumulativeRotation, setCumulativeRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const avatarRef = useRef(null);

  const initials = (name || '')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  const handleClick = () => {
    if (isMobile) return; // Don't spin on mobile
    if (!isSpinning) {
      setIsSpinning(true);
      setActiveFace((prevFace) => (prevFace + 1) % 3);
      setCumulativeRotation(prevRotation => prevRotation - 120); // Continue rotating in the same direction
      // Reset spinning state after animation completes
      setTimeout(() => setIsSpinning(false), 600); // 600ms matches CSS transition
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const containerStyle = {
    width: size,
    height: size,
    position: 'relative',
    cursor: 'default', // Changed to 'default' for easter egg feel
    marginRight: 12,
    perspective: '1000px', // Important for 3D effect
  };

  // Calculate translateZ for prism faces
  // R = (face_width / 2) / tan(PI / num_faces)
  // Here, face_width is 'size', num_faces is 3. tan(PI/3) = sqrt(3)
  const translateZValue = (size / 2) / Math.sqrt(3);

  const avatarStyle = {
    width: '100%',
    height: '100%',
    position: 'relative',
    transformStyle: 'preserve-3d', // Crucial for 3D children
    transition: 'transform 0.6s',
    transform: `rotateY(${cumulativeRotation}deg)`, // Use cumulative rotation
  };

  const faceBaseStyle = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden', // Hide faces when they are not facing front
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    userSelect: 'none', // Prevent highlighting/selection on click
  };

  const initialsStyle = {
    fontSize: size / 2,
    fontWeight: 'bold',
    color: '#fff',
    userSelect: 'none',
    textTransform: 'uppercase',
  };

  const logoImageStyle = {
    width: '100%', // Adjusted from 280% and scale(0.7) for simplicity with padding
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div style={containerStyle}>
      <div
        ref={avatarRef}
        style={avatarStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        title={name}
        aria-label={name}
      >
        {/* Face 1: Agent Headshot */}
        <div style={{
          ...faceBaseStyle,
          transform: `rotateY(0deg) translateZ(${translateZValue}px)`,
          backgroundColor: imageError ? '#3b6ea5' : 'transparent',
        }}>
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={`${name} headshot`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={handleImageError}
            />
          ) : (
            <div style={initialsStyle}>
              {initials}
            </div>
          )}
        </div>

        {/* Face 2: Balloon Logo */}
        <div style={{
          ...faceBaseStyle,
          transform: `rotateY(120deg) translateZ(${translateZValue}px)`,
          backgroundColor: '#f8f9fa',
          padding: '0%', // Removed padding to maximize balloon logo
        }}>
          <img
            src="/images/rmxballoons.png"
            alt="REMAX Balloon Logo"
            style={{
              ...logoImageStyle,
              transform: 'scale(1.5)', // Set balloon logo size to 1.5x
            }}
          />
        </div>

        {/* Face 3: JS Logo */}
        <div style={{
          ...faceBaseStyle,
          transform: `rotateY(240deg) translateZ(${translateZValue}px)`,
          backgroundColor: '#f8f9fa',
          padding: '5%', // Reduced padding for larger logo
        }}>
          <img
            src="/images/John Snippen_Logo_Black.jpg"
            alt="John Snippen Logo"
            style={logoImageStyle}
          />
        </div>
      </div>
    </div>
  );
}
